import { useState } from 'react';
import { usePopup } from '@/contexts/PopupContext';
import { Check, FileText, Search, Send, UserPlus, Scale, FolderKanban } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';

// Self Filing Tab - Step Cards Data
const selfFilingSteps = [
  {
    title: 'Application Preparation',
    icon: FileText,
    description: 'We prepare your trademark application on the USPTO TEAS system, ensuring accurate classification, proper formatting, and compliance with USPTO requirements.',
  },
  {
    title: 'Identity Verification & Review',
    icon: Search,
    description: "You securely verify your identity via ID.me and review the prepared application by our IP attorney. You'll have the opportunity to review all details and request any revisions before submission.",
  },
  {
    title: 'Filing',
    icon: Send,
    description: 'Once you review and approve the application, we will submit it on your behalf, ensuring all required details are accurately filed and in full compliance with official requirements of the USPTO.',
  },
];

// Attorney-Led Tab - Step Cards Data
const attorneyLedSteps = [
  {
    title: 'Information Collection',
    icon: UserPlus,
    description: 'We collect your trademark details, including name, logo (if applicable), and a brief description of your products/services.',
  },
  {
    title: 'Attorney Review & Application Drafting',
    icon: Scale,
    description: 'Our IP attorney reviews your case, determines the appropriate trademark class(es), and prepares a strong, USPTO-compliant application.',
  },
  {
    title: 'Filing & Case Management',
    icon: FolderKanban,
    description: 'We file the application on your behalf and handle all procedural steps, including monitoring your application and managing any USPTO correspondence.',
  },
];

// Self Filing - 4 Pricing Plans
const selfFilingPlans = [
  {
    name: 'Basic',
    price: '$45',
    fee: 'Govt. filing fee not included',
    description: 'Easy guided process where experts support your steps and ensure correctness',
    features: [
      'Case Review',
      'Case Filing',
      'Basic Search',
      'State E-Filing',
      'Trademark Secured',
    ],
    note: '',
    recommended: false,
  },
  {
    name: 'Deluxe',
    price: '$145',
    fee: 'Govt. filing fee not included',
    description: 'Balanced support system helping you move forward with clear professional guidance',
    features: [
      'Case Review',
      'Case Filing',
      'Basic Search',
      'State E-Filing',
      'Trademark Secured',
      'Direct Hit Search',
      'Dedicated Case Manager',
    ],
    note: '',
    recommended: true,
    badge: 'CORE OFFERING',
  },
  {
    name: 'Premium',
    price: '$299',
    fee: 'Govt. filing fee not included',
    description: 'Advanced assistance structure offering deeper help planning review and ongoing clarity',
    features: [
      'Case Review (by a TM Expert)',
      'Basic Free Trademark Search',
      'Trademark Monitoring Services (3 Months)',
      'Trademark Secured',
      'Trademark Secured',
      'Refusal Risk Meter',
      'Case filing service with complete paperwork',
    ],
    note: '',
    recommended: false,
  },
  {
    name: 'Imperial',
    price: '$599',
    fee: 'Govt. filing fee not included',
    description: 'Premium guided experience with expert leadership attention and complete confidence',
    features: [
      'Case Review & Filing (U.S Registered IP Attorney)',
      'Assigned Case Filing Officer',
      'Direct Hit Search',
      'Private Registration',
      'Trademark Secured',
      'Federal Search & Clearance Report (All States)',
      'Federal E-Filing (USPTO)',
      'Cease & Desist Letter',
      'Same Day Filing',
      'Trademark Monitoring (1 Year)', 
      'Trademark Secured',
      'Dedicated Case Manager',
      'Trademark Monitoring (1 Year)',
      '24/7 Secure Online Access',
      '100% Approval Guarantee',
    ],
    note: '',
    recommended: false,
    badge: 'FLAGSHIP SERVICE',
  },
];

// Attorney-Led - 4 Pricing Plans
const attorneyLedPlans = [
  {
    name: 'Basic',
    price: '$795',
    fee: 'Govt. filing fee not included',
    description: 'Simple reliable service guided by professionals for smooth filing completion process',
    features: ['Attorney-Led Case Review', 'Professional Application Drafting', 'Basic Search', 'USPTO E-Filing', 'Trademark Secured'],
    note: '',
    recommended: false,
  },
  {
    name: 'Deluxe',
    price: '$895',
    fee: 'Govt. filing fee not included',
    description: 'Better guided experience with added care support and improved overall confidence',
    features: ['Attorney-Led Case Review', 'Professional Application Drafting', 'Comprehensive Search', 'Trademark Alert', 'Trademark Secured', 'High Satisfaction Rate', 'Refusal Risk Meter'],
    note: '',
    recommended: true,
    badge: 'CORE OFFERING',
  },
  {
    name: 'Premium',
    price: '$1,145',
    fee: 'Govt. filing fee not included',
    description: 'Strong structured service with expert attention enhanced handling and steady progress',
    features: ['Attorney-Led Case Review', 'Professional Application Drafting', 'Comprehensive Search', 'Trademark Alert', 'Trademark Secured', 'High Satisfaction Rate', 'Refusal Risk Meter', 'Trademark Monitoring', 'Complete Documentation', 'Digital File', 'Dedicated Case Manager'],
    note: '',
    recommended: false,
  },
  {
    name: 'Imperial',
    price: '$1,545',
    fee: 'Government filing fee included',
    description: 'Premium priority service with highest focus care speed and complete assurance',
    features: ['Attorney-Led Case Review', 'Professional Application Drafting', 'Comprehensive Search', 'Trademark Alert', 'Trademark Secured', 'High Satisfaction Rate', 'Refusal Risk Meter', 'Trademark Monitoring', 'Complete Documentation', 'Digital File', 'Dedicated Case Manager', 'High Approval Rate', 'Comprehensive Search Report', 'High Priority Service', 'Cease & Desist Letter', 'Office Action Response'],
    note: '',
    recommended: false,
    badge: 'FLAGSHIP SERVICE',
  },
];

// Step Card Component
function StepCard({ title, Icon, description }) {
  return (
    <div className="bg-white rounded-xl border border-emerald-100 p-6 text-center hover:shadow-md transition-shadow duration-300">
      <div className="w-12 h-12 rounded-full bg-[#F0FDF4] flex items-center justify-center mx-auto mb-4">
        <Icon className="w-6 h-6 text-[#064E3B]" />
      </div>
      <h3 className="font-heading font-semibold text-[#064E3B] mb-2">{title}</h3>
      <p className="text-sm text-slate-600">{description}</p>
    </div>
  );
}

// Pricing Card Component
function PricingCard({ plan, onSelect, tabType }) {
  return (
    <div
      className={`relative rounded-2xl bg-white flex flex-col ${
        plan.recommended
          ? 'pricing-recommended ring-2 ring-[#064E3B] scale-[1.02] z-10'
          : 'border border-slate-200 hover:border-emerald-200'
      } transition-all duration-300`}
      data-testid={`pricing-plan-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {plan.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#064E3B] text-white text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap">
          {plan.badge}
        </div>
      )}

      <div className={`p-6 pb-4 ${plan.recommended ? 'bg-[#064E3B] rounded-t-2xl text-white' : ''}`}>
        <div className={`text-3xl font-heading font-extrabold mb-1 ${plan.recommended ? 'text-white' : 'text-[#064E3B]'}`}>
          {plan.price}
        </div>
        <p className={`text-xs mb-4 ${plan.recommended ? 'text-emerald-200' : 'text-slate-500'}`}>
          {plan.fee}
        </p>
        <h3 className={`font-heading font-bold text-base mb-2 ${plan.recommended ? 'text-white' : 'text-[#064E3B]'}`}>
          {plan.name}
        </h3>
        {plan.description && (
          <p className={`text-sm leading-relaxed ${plan.recommended ? 'text-emerald-100' : 'text-slate-600'}`}>
            {plan.description}
          </p>
        )}
      </div>

      <div className="flex-1 p-6 pt-4">
        <div className="max-h-[260px] overflow-y-auto hide-scrollbar space-y-2.5">
          {plan.features.map((feature, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <Check className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span className="text-sm text-slate-600">{feature}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-4 text-center italic">{plan.note}</p>
      </div>

      <div className="p-6 pt-0">
        <button
          onClick={() => onSelect({ title: plan.name, amount: parseInt(plan.price.replace('$', '')), tabType })}
          className={`w-full py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
            plan.recommended
              ? 'bg-[#064E3B] text-white hover:bg-[#065F46] btn-pulse'
              : 'bg-emerald-50 text-[#064E3B] hover:bg-emerald-100 border border-emerald-200'
          }`}
          data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
        >
          Protect My Brand Now
        </button>
      </div>
    </div>
  );
}

// Tab Content Component
function TabContent({ steps, plans, title, subtitle, onSelectPlan, tabType }) {
  return (
    <div className="space-y-12">
      {/* Step Cards - Desktop Grid */}
      <div className="hidden md:grid md:grid-cols-3 gap-6">
        {steps.map((step, index) => (
          <StepCard key={index} title={step.title} Icon={step.icon} description={step.description} />
        ))}
      </div>

      {/* Step Cards - Mobile Carousel */}
      <div className="md:hidden">
        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {steps.map((step, index) => (
              <CarouselItem key={index} className="pl-4 basis-[100%]">
                <StepCard title={step.title} Icon={step.icon} description={step.description} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center gap-2 mt-4">
            <CarouselPrevious className="relative inset-0 translate-x-0 translate-y-0 border-emerald-200 text-[#064E3B] hover:bg-emerald-50 h-8 w-8" />
            <CarouselNext className="relative inset-0 translate-x-0 translate-y-0 border-emerald-200 text-[#064E3B] hover:bg-emerald-50 h-8 w-8" />
          </div>
        </Carousel>
      </div>

      {/* Pricing Section Heading */}
      <div className="text-center">
        <h3 className="font-heading font-bold text-2xl sm:text-3xl text-[#064E3B] mb-2">{title}</h3>
        <p className="text-slate-600 text-base">{subtitle}</p>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <PricingCard key={plan.name} plan={plan} onSelect={onSelectPlan} tabType={tabType} />
        ))}
      </div>
    </div>
  );
}

export default function PricingSection() {
  const [activeTab, setActiveTab] = useState('self');
  const { openPopup } = usePopup();

  const handleSelectPlan = (planData) => {
    // Pass both plan info and tab type for tmSignUpType
    openPopup({
      title: planData.title,
      amount: planData.amount,
      tmSignUpType: planData.tabType === 'attorney' ? 2 : 1,
    });
  };

  return (
    <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro Section */}
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#064E3B] mb-6">
            <span className="text-[#10B981]">Filing Options</span> That Fit Your Needs
          </h2>
          <p className="text-slate-600 text-base lg:text-lg max-w-4xl mx-auto leading-relaxed">
            From trademark registrations to ongoing monitoring, renewals, and more, our services at [Brand Name] are designed to secure your brand. Whether you need to register a business name, trademark a logo, or file a trademark for a slogan, we are here to assist. Our services cover all 50 U.S. states and extend globally, ensuring your intellectual property remains protected no matter where you are.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-12">
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white rounded-full p-1 shadow-sm border border-emerald-100">
              <button
                onClick={() => setActiveTab('self')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                  activeTab === 'self'
                    ? 'bg-[#064E3B] text-white'
                    : 'text-[#064E3B] hover:bg-emerald-50'
                }`}
                data-testid="tab-self-filing"
              >
                Self Filing with Assistance
              </button>
              <button
                onClick={() => setActiveTab('attorney')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                  activeTab === 'attorney'
                    ? 'bg-[#064E3B] text-white'
                    : 'text-[#064E3B] hover:bg-emerald-50'
                }`}
                data-testid="tab-attorney-led"
              >
                IP Attorney-Led Filing
              </button>
            </div>
          </div>

          {/* Tab Content with Animation */}
          <div className="transition-all duration-300 ease-in-out">
            {activeTab === 'self' ? (
              <TabContent
                steps={selfFilingSteps}
                plans={selfFilingPlans}
                title={<>Self-Filing with Expert Assistance<br />and Complete Support Plan for Easy Filing</>}
                subtitle=""
                onSelectPlan={handleSelectPlan}
                tabType="self"
              />
            ) : (
              <TabContent
                steps={attorneyLedSteps}
                plans={attorneyLedPlans}
                title={<>Attorney Led Filing with Complete<br />End-to-End Service Plan</>}
                subtitle=""
                onSelectPlan={handleSelectPlan}
                tabType="attorney"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
